/**
 * S15 · AI Suggestions — bottom-sheet modal over FormFill. Derives
 * candidate values for the active field from the session's captures
 * and applies the chosen one via `useUpsertFieldValueMutation`.
 *
 * This sheet makes no network call of its own, and that is the design
 * working rather than a gap: the server extracts on upload and writes to
 * `captures.ai_fields`, the pull path lands it in SQLite, and
 * `suggestionsFor` reads it back off the capture. So suggestions are
 * available offline, on exactly the captures the inspector can see.
 *
 * What it cannot show is a field the alias map in `@kapture/domain`'s
 * `suggestions.ts` doesn't reach — matching is by field id, so a template
 * field named outside that map gets nothing even when the model extracted it.
 */
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Btn, Chip, I, Ico, T } from '@/components/primitives';
import {
  useCapturesBySession,
  useSession,
  useUpsertFieldValueMutation,
} from '@kapture/client-core';
import {
  getFieldDef,
  getTemplate,
  suggestionsFor,
  whyNoSuggestion,
  type CaptureAiState,
  type Suggestion,
} from '@kapture/domain';

export const Route = createFileRoute('/sessions/$sessionId/form/$fieldId/suggestions')({
  component: SuggestionsSheet,
  staticData: { mobile: true, modal: true, fullscreen: true },
});

function SuggestionsSheet() {
  const { sessionId, fieldId } = Route.useParams();
  const navigate = useNavigate();
  const { data: session } = useSession(sessionId);
  const { data: captures } = useCapturesBySession(sessionId);
  const upsert = useUpsertFieldValueMutation();

  const template = getTemplate(session?.templateId);
  const field = template ? getFieldDef(template, fieldId) : null;

  const suggestions = suggestionsFor(fieldId, captures ?? []);
  // Why there is nothing, when there is nothing. Five different situations used
  // to render as one empty list, and only one of them is a problem.
  const quiet = whyNoSuggestion(captures ?? []);

  function close() {
    navigate({
      to: '/sessions/$sessionId/form/$fieldId',
      params: { sessionId, fieldId },
    });
  }

  async function accept(s: Suggestion) {
    await upsert.mutateAsync({
      sessionId,
      fieldId: s.fieldId,
      value: s.value,
      source: s.source,
      confidence: s.confidence,
      sourceCaptureId: s.sourceCaptureId,
    });
    close();
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="AI suggestions"
      onClick={close}
      style={{
        position: 'absolute',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        fontFamily: T.font,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: T.surface,
          borderRadius: '20px 20px 0 0',
          padding: '12px 16px 20px',
          maxHeight: '85%',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 -20px 40px rgba(0,0,0,0.15)',
          color: T.text,
        }}
      >
        <div
          style={{
            width: 36,
            height: 4,
            borderRadius: 2,
            background: T.stroke2,
            margin: '0 auto 14px',
          }}
        />

        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            marginBottom: 14,
          }}
        >
          {/* AI-assist marker colour, not Harvest Flame — a machine
              suggestion is not a decisive state until a person applies it. */}
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              background: '#EFECFB',
              color: '#7B6BE0',
              border: '1px solid #7B6BE055',
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <Ico d={I.sparkle} size={18} fill="#7B6BE0" stroke="#7B6BE0" />
          </div>
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontSize: 17,
                fontWeight: 700,
                letterSpacing: '-0.02em',
              }}
            >
              AI suggestions
            </div>
            <div style={{ fontSize: 12, color: T.textDim, marginTop: 2 }}>
              {field?.label ?? 'Field'} ·{' '}
              {suggestions.length === 0
                ? 'no matches in this session yet'
                : `${suggestions.length} candidate${suggestions.length === 1 ? '' : 's'}`}
            </div>
          </div>
          <button
            type="button"
            onClick={close}
            aria-label="Close"
            style={{
              width: 32,
              height: 32,
              borderRadius: 16,
              background: T.surface2,
              border: 'none',
              color: T.textDim,
              display: 'grid',
              placeItems: 'center',
              cursor: 'pointer',
            }}
          >
            <Ico d={I.close} size={14} />
          </button>
        </div>

        <div style={{ flex: 1, overflow: 'auto' }}>
          {suggestions.length === 0 ? (
            <EmptyHint state={quiet} />
          ) : (
            suggestions.map((s) => (
              <SuggestionCard
                key={s.id}
                suggestion={s}
                onAccept={() => void accept(s)}
                disabled={upsert.isPending}
              />
            ))
          )}
        </div>

        <div style={{ marginTop: 10 }}>
          <Btn tone="light" size="md" full onClick={close}>
            Back to field
          </Btn>
        </div>
      </div>
    </div>
  );
}

function SuggestionCard({
  suggestion,
  onAccept,
  disabled,
}: {
  suggestion: Suggestion;
  onAccept(): void;
  disabled: boolean;
}) {
  const conf = Math.round(suggestion.confidence * 100);
  const tone = conf >= 90 ? 'good' : conf >= 70 ? 'info' : 'warn';
  return (
    <div
      style={{
        padding: 14,
        borderRadius: 12,
        border: `1px solid ${T.stroke}`,
        background: T.surface,
        boxShadow: T.sh,
        marginBottom: 8,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 6,
        }}
      >
        <div
          style={{
            fontSize: 10,
            color: T.textMute,
            fontFamily: T.mono,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            fontWeight: 600,
          }}
        >
          {suggestion.origin}
        </div>
        <Chip tone={tone} size="sm">
          {conf}%
        </Chip>
      </div>
      <div
        style={{
          fontSize: 18,
          fontWeight: 700,
          letterSpacing: '-0.01em',
          wordBreak: 'break-word',
          marginBottom: 6,
        }}
      >
        {String(suggestion.value ?? '—')}
      </div>
      <div
        style={{
          fontSize: 11,
          color: T.textDim,
          lineHeight: 1.5,
          marginBottom: 10,
        }}
      >
        {suggestion.rationale}
      </div>
      <Btn
        tone="primary"
        size="sm"
        full
        onClick={onAccept}
        disabled={disabled}
        iconRight={<Ico d={I.check2} size={14} sw={2.4} />}
        style={{ opacity: disabled ? 0.5 : 1 }}
      >
        Apply suggestion
      </Btn>
    </div>
  );
}

/**
 * What "no suggestions" actually means, this time.
 *
 * The screen used to say one thing for five situations: the model had read the
 * captures and reported nothing, extraction was switched off for the org, the
 * daily cap was reached, a run failed, or nothing had been captured yet. They
 * call for different next actions and one of them is not a problem at all — a
 * model that read a photograph and declined to guess is the behaviour the
 * prompts ask for, and showing that as an apology teaches the inspector to
 * distrust a system that was working.
 */
function EmptyHint({ state }: { state: CaptureAiState }) {
  const copy: Record<CaptureAiState['outcome'], { title: string; body: string }> = {
    pending: {
      title: 'Nothing to suggest yet',
      body: 'Capture a photo, voice note, or barcode in this session — matching values are offered here for you to review and apply. Nothing is written to the form without your say-so.',
    },
    abstained: {
      title: 'Read, and nothing to suggest',
      body: 'The model looked at this session’s captures and found no value it was confident enough to offer for this field. That is a considered answer, not a failure — a closer photo of the plate or label is the thing most likely to change it.',
    },
    // Unreachable from this screen: something was extracted, so the sheet has
    // cards to render. Present so the map is total rather than defaulted.
    extracted: {
      title: 'Nothing matching this field',
      body: 'Values were extracted from this session’s captures, but none of them answer this field.',
    },
    skipped: {
      title: 'Not analysed',
      body: 'These captures were not sent for analysis, so there is nothing to offer here. Fill the field in yourself — nothing is blocked.',
    },
    failed: {
      title: 'Analysis did not finish',
      body: 'Something went wrong analysing these captures. It will be retried; in the meantime fill the field in yourself.',
    },
  };
  const { title, body } = copy[state.outcome];
  return (
    <div
      style={{
        padding: '28px 16px',
        border: `1px dashed ${T.stroke2}`,
        borderRadius: 12,
        color: T.textDim,
        textAlign: 'center',
        fontSize: 13,
        lineHeight: 1.5,
      }}
    >
      <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 6 }}>{title}</div>
      {body}
      {state.detail ? (
        <div
          style={{
            marginTop: 10,
            fontFamily: T.mono,
            fontSize: 11,
            color: T.textMute,
            wordBreak: 'break-word',
          }}
        >
          {state.detail}
        </div>
      ) : null}
    </div>
  );
}
